<template>
  <el-dialog title="功率曲线" :visible.sync="show" :before-close="closeDialog" width="1200px">
    <div v-loading="loading">
      <!-- 设备信息 -->
      <el-descriptions class="mb-20" title="设备信息" :column="4" border>
        <el-descriptions-item label="资产码">{{ deviceInfo.asset_code || "--" }}</el-descriptions-item>
        <el-descriptions-item label="设备名称">{{ deviceInfo.re_dev_name || "--" }}</el-descriptions-item>
        <el-descriptions-item label="设备类型">{{ deviceInfo.re_dev_type || "--" }}</el-descriptions-item>
        <el-descriptions-item label="当前状态">
          <el-tag v-if="deviceInfo.status_name === '在线'" type="success">在线</el-tag>
          <el-tag v-else-if="deviceInfo.status_name === '离线'" type="danger">离线</el-tag>
          <span v-else>--</span>
        </el-descriptions-item>
      </el-descriptions>

      <!-- 时间范围选择 -->
      <el-descriptions class="mb-10" title="时间范围" :column="4" border>
        <el-descriptions-item label="时间范围">
          <el-select v-model="timeRange" @change="handleTimeRangeChange" size="small" style="width: 120px">
            <el-option label="最近1小时" value="1h"></el-option>
            <el-option label="最近6小时" value="6h"></el-option>
            <el-option label="最近24小时" value="24h"></el-option>
            <el-option label="最近7天" value="7d"></el-option>
            <el-option label="最近30天" value="30d"></el-option>
            <el-option label="自定义" value="custom"></el-option>
          </el-select>
        </el-descriptions-item>
        <el-descriptions-item label="开始时间" v-if="timeRange === 'custom'">
          <el-date-picker v-model="customStartTime" type="datetime" placeholder="选择开始时间" size="small" style="width: 190px" :picker-options="startPickerOptions" @change="handleCustomTimeChange"></el-date-picker>
        </el-descriptions-item>
        <el-descriptions-item label="结束时间" v-if="timeRange === 'custom'">
          <el-date-picker v-model="customEndTime" type="datetime" placeholder="选择结束时间" size="small" style="width: 190px" :picker-options="endPickerOptions" @change="handleCustomTimeChange"></el-date-picker>
        </el-descriptions-item>
        <el-descriptions-item label="更新时间">
          {{ lastUpdateTime || "--" }}
        </el-descriptions-item>
      </el-descriptions>

      <!-- 时间间隔提示 -->
      <el-alert v-if="timeSpanInfo" :title="timeSpanInfo" type="info" :closable="false" class="mb-20" style="margin-top: -7px; padding: 8px 16px; font-size: 12px"> </el-alert>

      <!-- 实时数据汇总 -->
      <el-descriptions class="mb-20" title="实时数据" :column="4" border>
        <el-descriptions-item label="A相电压">
          <span class="bold-text">{{ formatVoltage(realTimeData.acVolA) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="A相电流">
          <span class="bold-text">{{ formatCurrent(realTimeData.acCurA) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="A相功率">
          <span class="bold-text">{{ formatPower(realTimeData.realAPower) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="A相温度">
          <span class="bold-text">{{ formatTemp(realTimeData.tempA) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="B相电压">
          <span class="bold-text">{{ formatVoltage(realTimeData.acVolB) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="B相电流">
          <span class="bold-text">{{ formatCurrent(realTimeData.acCurB) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="B相功率">
          <span class="bold-text">{{ formatPower(realTimeData.realBPower) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="B相温度">
          <span class="bold-text">{{ formatTemp(realTimeData.tempB) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="C相电压">
          <span class="bold-text">{{ formatVoltage(realTimeData.acVolC) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="C相电流">
          <span class="bold-text">{{ formatCurrent(realTimeData.acCurC) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="C相功率">
          <span class="bold-text">{{ formatPower(realTimeData.realCPower) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="C相温度">
          <span class="bold-text">{{ formatTemp(realTimeData.tempC) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="截止时间">
          <span class="bold-text">{{ formatElectricity(realTimeData.create_time) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="总电量">
          <span class="bold-text">{{ formatTotalElectricity(realTimeData.totalElect) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="总功率">
          <span class="bold-text red-text">{{ formatPower(realTimeData.realtotalPower) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="N相温度">
          <span class="bold-text">{{ formatTemp(realTimeData.tempN) }}</span>
        </el-descriptions-item>
      </el-descriptions>

      <!-- 图表展示 -->
      <el-descriptions class="mb-20 curve-chart" title="曲线图表">
        <el-descriptions-item>
          <div class="charts-container">
            <div class="chart-item">
              <div class="chart-title">电压变化曲线</div>
              <div class="chart-wrapper" ref="voltageChart"></div>
            </div>
            <div class="chart-item">
              <div class="chart-title">电流变化曲线</div>
              <div class="chart-wrapper" ref="currentChart"></div>
            </div>
            <div class="chart-item">
              <div class="chart-title">功率变化曲线</div>
              <div class="chart-wrapper" ref="powerChart"></div>
            </div>
            <div class="chart-item">
              <div class="chart-title">温度变化曲线</div>
              <div class="chart-wrapper" ref="tempChart"></div>
            </div>
          </div>
        </el-descriptions-item>
      </el-descriptions>
    </div>

    <span slot="footer">
      <el-button @click="closeDialog">关闭</el-button>
      <el-button type="primary" @click="refreshData">刷新数据</el-button>
      <el-button type="success" @click="exportData">导出数据</el-button>
    </span>
  </el-dialog>
</template>

<script>
import * as echarts from "echarts"
import { GetPlatFormHisList } from "@/api/dev_data_view"

export default {
  name: "PowerCurve",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      timeRange: "24h",
      customStartTime: null,
      customEndTime: null,
      lastUpdateTime: "",
      timeSpanInfo: "", // 时间范围提示信息
      deviceInfo: {},
      realTimeData: {},

      voltageChart: null,
      currentChart: null,
      powerChart: null,
      tempChart: null,

      chartData: {
        time: [],
        voltageA: [],
        voltageB: [],
        voltageC: [],
        currentA: [],
        currentB: [],
        currentC: [],
        powerA: [],
        powerB: [],
        powerC: [],
        powerTotal: [],
        tempA: [],
        tempB: [],
        tempC: [],
        tempN: []
      },

      startPickerOptions: {
        // 开始时间不能晚于结束时间
        // disabledDate: (time) => {
        //   if (this.customEndTime) {
        //     return time.getTime() > this.customEndTime.getTime()
        //   }
        //   return false
        // }
      },
      endPickerOptions: {
        // 结束时间不能早于开始时间
        // disabledDate: (time) => {
        //   if (this.customStartTime) {
        //     return time.getTime() < this.customStartTime.getTime()
        //   }
        //   return false
        // }
      }
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.initDialog()
      } else {
        this.disposeCharts()
      }
    }
  },
  methods: {
    // 数据转换方法
    transformData(originalData) {
      if (!originalData) return originalData

      const transformed = { ...originalData }

      // 电压除以10
      if (transformed.acVolA !== undefined && transformed.acVolA !== null) {
        transformed.acVolA = transformed.acVolA / 10
      }
      if (transformed.acVolB !== undefined && transformed.acVolB !== null) {
        transformed.acVolB = transformed.acVolB / 10
      }
      if (transformed.acVolC !== undefined && transformed.acVolC !== null) {
        transformed.acVolC = transformed.acVolC / 10
      }

      // 电流除以100
      if (transformed.acCurA !== undefined && transformed.acCurA !== null) {
        transformed.acCurA = transformed.acCurA / 100
      }
      if (transformed.acCurB !== undefined && transformed.acCurB !== null) {
        transformed.acCurB = transformed.acCurB / 100
      }
      if (transformed.acCurC !== undefined && transformed.acCurC !== null) {
        transformed.acCurC = transformed.acCurC / 100
      }

      // 温度除以10
      if (transformed.tempA !== undefined && transformed.tempA !== null) {
        transformed.tempA = transformed.tempA / 100
      }
      if (transformed.tempB !== undefined && transformed.tempB !== null) {
        transformed.tempB = transformed.tempB / 100
      }
      if (transformed.tempC !== undefined && transformed.tempC !== null) {
        transformed.tempC = transformed.tempC / 100
      }
      if (transformed.tempN !== undefined && transformed.tempN !== null) {
        transformed.tempN = transformed.tempN / 100
      }

      // 功率除以100
      if (transformed.realAPower !== undefined && transformed.realAPower !== null) {
        transformed.realAPower = transformed.realAPower / 100
      }
      if (transformed.realBPower !== undefined && transformed.realBPower !== null) {
        transformed.realBPower = transformed.realBPower / 100
      }
      if (transformed.realCPower !== undefined && transformed.realCPower !== null) {
        transformed.realCPower = transformed.realCPower / 100
      }
      if (transformed.realtotalPower !== undefined && transformed.realtotalPower !== null) {
        transformed.realtotalPower = transformed.realtotalPower / 100
      }

      return transformed
    },

    // 初始化弹窗
    initDialog() {
      this.resetData()
      this.loading = true
      this.fetchHistoryData()
    },

    // 重置数据
    resetData() {
      this.deviceInfo = {}
      this.realTimeData = {}
      this.chartData = {
        time: [],
        voltageA: [],
        voltageB: [],
        voltageC: [],
        currentA: [],
        currentB: [],
        currentC: [],
        powerA: [],
        powerB: [],
        powerC: [],
        powerTotal: [],
        tempA: [],
        tempB: [],
        tempC: [],
        tempN: []
      }
      this.customStartTime = null
      this.customEndTime = null
      this.lastUpdateTime = ""
      this.timeSpanInfo = ""
    },

    // 获取历史数据
    async fetchHistoryData() {
      try {
        this.deviceInfo = this.params
        const params = {
          assetsCode: this.params.asset_code
        }

        const { startTime, endTime } = this.calculateTimeRange()
        params.start_time = this.formatDateTimeForApi(startTime)
        params.end_time = this.formatDateTimeForApi(endTime)

        // 计算并显示时间间隔
        const timeSpan = endTime.getTime() - startTime.getTime()
        this.timeSpanInfo = this.formatTimeSpan(timeSpan)

        const res = await GetPlatFormHisList(params)

        if (res.data && res.data.length > 0) {
          this.processChartData(res.data, startTime, endTime)
          // 获取最后一条数据并转换
          const lastData = res.data[res.data.length - 1]
          this.realTimeData = this.transformData(lastData)
          this.$nextTick(() => {
            this.initCharts(startTime, endTime)
          })
        } else {
          this.clearChartData()
          this.initCharts()
          this.$message.warning("此时间段内暂无数据")
        }

        this.lastUpdateTime = this.formatDateTime(new Date())
      } catch (error) {
        console.error("获取历史数据失败:", error)
        this.clearChartData()
        this.$message.error("获取数据失败")
      } finally {
        this.loading = false
      }
    },

    // 计算时间范围
    calculateTimeRange() {
      const now = new Date()
      let startTime = new Date()

      switch (this.timeRange) {
        case "1h":
          startTime = new Date(now.getTime() - 1 * 60 * 60 * 1000) // 1小时前
          break
        case "6h":
          startTime = new Date(now.getTime() - 6 * 60 * 60 * 1000) // 6小时前
          break
        case "24h":
          startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000) // 24小时前
          break
        case "7d":
          startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000) // 7天前
          break
        case "30d":
          startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000) // 30天前
          break
        case "custom":
          if (this.customStartTime && this.customEndTime) {
            return {
              startTime: this.customStartTime,
              endTime: this.customEndTime
            }
          } else {
            // 默认自定义时间范围为最近1小时
            const defaultStart = new Date(now.getTime() - 1 * 60 * 60 * 1000)
            this.customStartTime = defaultStart
            this.customEndTime = now
            return {
              startTime: defaultStart,
              endTime: now
            }
          }
      }

      return { startTime, endTime: now }
    },

    // 处理图表数据
    processChartData(data, startTime, endTime) {
      this.clearChartData()

      data.forEach((item, index) => {
        // 转换数据
        const transformedItem = this.transformData(item)
        const time = transformedItem.create_time || transformedItem.record_time || this.formatTimeIndex(index, data.length, startTime, endTime)
        this.chartData.time.push(time)

        this.chartData.voltageA.push(transformedItem.acVolA || 0)
        this.chartData.voltageB.push(transformedItem.acVolB || 0)
        this.chartData.voltageC.push(transformedItem.acVolC || 0)
        this.chartData.currentA.push(transformedItem.acCurA || 0)
        this.chartData.currentB.push(transformedItem.acCurB || 0)
        this.chartData.currentC.push(transformedItem.acCurC || 0)
        this.chartData.powerA.push(transformedItem.realAPower || 0)
        this.chartData.powerB.push(transformedItem.realBPower || 0)
        this.chartData.powerC.push(transformedItem.realCPower || 0)
        this.chartData.powerTotal.push(transformedItem.realtotalPower || 0)
        this.chartData.tempA.push(transformedItem.tempA || 0)
        this.chartData.tempB.push(transformedItem.tempB || 0)
        this.chartData.tempC.push(transformedItem.tempC || 0)
        this.chartData.tempN.push(transformedItem.tempN || 0)
      })
    },

    // 清空图表数据
    clearChartData() {
      Object.keys(this.chartData).forEach((key) => {
        this.chartData[key] = []
      })
    },

    // 初始化所有图表
    initCharts(startTime, endTime) {
      this.initChart("voltageChart", "电压(V)", ["A相电压", "B相电压", "C相电压"], ["voltageA", "voltageB", "voltageC"], startTime, endTime)
      this.initChart("currentChart", "电流(A)", ["A相电流", "B相电流", "C相电流"], ["currentA", "currentB", "currentC"], startTime, endTime)
      this.initChart("powerChart", "功率(kW)", ["A相功率", "B相功率", "C相功率", "总功率"], ["powerA", "powerB", "powerC", "powerTotal"], startTime, endTime, ["#5470c6", "#91cc75", "#fac858", "#ee6666"])
      this.initChart("tempChart", "温度(°C)", ["A相温度", "B相温度", "C相温度", "N相温度"], ["tempA", "tempB", "tempC", "tempN"], startTime, endTime)
    },

    // 初始化单个图表
    initChart(refName, yAxisName, legendData, dataKeys, startTime, endTime, colors = ["#5470c6", "#91cc75", "#fac858"]) {
      if (!this.$refs[refName]) return

      if (this[refName]) {
        this[refName].dispose()
      }

      this[refName] = echarts.init(this.$refs[refName])

      const series = legendData.map((name, index) => ({
        name,
        type: "line",
        smooth: true,
        data: this.chartData[dataKeys[index]],
        lineStyle: { color: colors[index] || colors[Math.min(index, colors.length - 1)], width: index === 3 ? 3 : 2 },
        symbol: "circle",
        symbolSize: index === 3 ? 5 : 4
      }))

      // 计算时间跨度
      const timeSpan = endTime.getTime() - startTime.getTime()
      const dataLength = this.chartData.time.length
      let interval = 0
      let rotate = 0

      // 根据时间跨度设置不同的显示策略
      if (timeSpan <= 2 * 60 * 60 * 1000) {
        // 2小时内
        interval = Math.max(Math.floor(dataLength / 8), 1) // 显示大约8个标签
      } else if (timeSpan <= 6 * 60 * 60 * 1000) {
        // 6小时内
        interval = Math.max(Math.floor(dataLength / 10), 1) // 显示大约10个标签
      } else if (timeSpan <= 24 * 60 * 60 * 1000) {
        // 24小时内
        interval = Math.max(Math.floor(dataLength / 12), 1) // 显示大约12个标签
      } else if (timeSpan <= 7 * 24 * 60 * 60 * 1000) {
        // 7天内
        interval = Math.max(Math.floor(dataLength / 8), 1) // 显示大约8个标签
        rotate = 45
      } else {
        // 超过7天
        interval = Math.max(Math.floor(dataLength / 6), 1) // 显示大约6个标签
        rotate = 45
      }

      const option = {
        tooltip: {
          trigger: "axis",
          confine: true,
          formatter: (params) => {
            let result = params[0].name + "<br/>"
            params.forEach((item) => {
              const unit = this.getUnitByChartType(refName, item.seriesName)
              result += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${item.color}"></span>`
              result += `${item.seriesName}: ${item.value} ${unit}<br/>`
            })
            return result
          }
        },
        legend: {
          data: legendData,
          top: 13,
          right: 20,
          itemWidth: 12,
          itemHeight: 12
        },
        grid: {
          left: "4%",
          right: "4%",
          bottom: rotate === 45 ? "12%" : "8%", // 旋转标签时增加底部间距
          top: "16%",
          containLabel: true
        },
        xAxis: {
          type: "category",
          data: this.chartData.time,
          axisLabel: {
            rotate: rotate,
            interval: interval,
            margin: 10,
            padding: [10, 0, 0, 0],
            formatter: (value) => {
              if (!value) return ""

              // 尝试解析各种时间格式
              let dateObj = null
              if (value.includes(" ") || value.includes("T") || value.includes("-")) {
                dateObj = new Date(value)
              } else if (value.includes(":")) {
                // 可能是只有时间的情况
                const now = new Date()
                const [hours, minutes] = value.split(":")
                dateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parseInt(hours), parseInt(minutes || 0))
              }

              if (dateObj && !isNaN(dateObj.getTime())) {
                const year = dateObj.getFullYear()
                const month = (dateObj.getMonth() + 1).toString().padStart(2, "0")
                const day = dateObj.getDate().toString().padStart(2, "0")
                const hours = dateObj.getHours().toString().padStart(2, "0")
                const minutes = dateObj.getMinutes().toString().padStart(2, "0")

                // 根据时间跨度决定显示格式
                if (timeSpan <= 2 * 60 * 60 * 1000) {
                  // 2小时内，显示到分钟
                  return `${hours}:${minutes}`
                } else if (timeSpan <= 6 * 60 * 60 * 1000) {
                  // 6小时内，显示小时:分钟
                  return `${hours}:${minutes}`
                } else if (timeSpan <= 24 * 60 * 60 * 1000) {
                  // 24小时内，显示小时:分钟
                  return `${hours}:${minutes}`
                } else if (timeSpan <= 7 * 24 * 60 * 60 * 1000) {
                  // 7天内，显示月-日
                  return `${month}-${day}`
                } else {
                  // 超过7天，显示年-月-日
                  return `${year}-${month}-${day}`
                }
              }

              return value
            }
          },
          axisTick: {
            alignWithLabel: true,
            length: 5
          },
          axisLine: {
            lineStyle: {
              color: "#ccc"
            }
          },
          nameTextStyle: {
            padding: [10, 0, 0, 0]
          }
        },
        yAxis: {
          type: "value",
          name: yAxisName,
          min: 0,
          axisLine: {
            lineStyle: {
              color: "#ccc"
            }
          },
          splitLine: {
            lineStyle: {
              type: "dashed",
              color: "#eee"
            }
          }
        },
        series
      }

      this[refName].setOption(option)
    },

    // 根据图表类型获取单位
    getUnitByChartType(chartType, seriesName) {
      switch (chartType) {
        case "voltageChart":
          return "V"
        case "currentChart":
          return "A"
        case "powerChart":
          return "kW"
        case "tempChart":
          return "°C"
        default:
          return ""
      }
    },

    // 格式化时间跨度
    formatTimeSpan(timeSpan) {
      const seconds = Math.floor(timeSpan / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (seconds < 60) {
        return `时间范围: ${seconds}秒`
      } else if (minutes < 60) {
        return `时间范围: ${minutes}分钟`
      } else if (hours < 24) {
        return `时间范围: ${hours}小时${minutes % 60}分钟`
      } else {
        return `时间范围: ${days}天${hours % 24}小时`
      }
    },

    // 销毁图表
    disposeCharts() {
      ;[this.voltageChart, this.currentChart, this.powerChart, this.tempChart].forEach((chart) => {
        if (chart) chart.dispose()
      })
    },

    // 处理时间范围变化
    handleTimeRangeChange() {
      if (this.timeRange === "custom") {
        // 自定义时间范围时，如果没有设置时间，则设置默认值为当前时间的前1小时
        if (!this.customStartTime || !this.customEndTime) {
          const now = new Date()
          this.customStartTime = new Date(now.getTime() - 1 * 60 * 60 * 1000)
          this.customEndTime = now
        }
        this.fetchHistoryData()
      } else {
        this.fetchHistoryData()
      }
    },

    // 处理自定义时间变化
    handleCustomTimeChange() {
      if (this.timeRange === "custom") {
        if (this.customStartTime && this.customEndTime) {
          if (this.customStartTime > this.customEndTime) {
            this.$message.warning("开始时间不能晚于结束时间")
            this.customEndTime = new Date(this.customStartTime.getTime())
            return
          }

          // 检查时间跨度是否合理
          const timeSpan = this.customEndTime.getTime() - this.customStartTime.getTime()
          if (timeSpan < 1 * 60 * 1000) {
            // 小于1分钟
            this.$message.warning("时间范围太短，请选择至少1分钟的时间范围")
            this.customEndTime = new Date(this.customStartTime.getTime() + 1 * 60 * 1000) // 设置为开始时间后1分钟
            return
          }

          this.fetchHistoryData()
        }
      }
    },

    // 刷新数据
    refreshData() {
      this.fetchHistoryData()
      this.$message.success("数据已刷新")
    },

    // 导出数据
    exportData() {
      // 导出转换后的数据
      const exportData = {
        设备信息: this.deviceInfo,
        实时数据: this.realTimeData,
        图表数据: this.chartData,
        导出时间: this.formatDateTime(new Date()),
        数据说明: "数据已按照以下规则转换：电压除以10，电流除以100，温度除以10，功率除以100"
      }

      const dataStr = JSON.stringify(exportData, null, 2)
      const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr)
      const exportFileName = `功率曲线_${this.deviceInfo.asset_code || "data"}_${new Date().getTime()}.json`

      const linkElement = document.createElement("a")
      linkElement.setAttribute("href", dataUri)
      linkElement.setAttribute("download", exportFileName)
      document.body.appendChild(linkElement)
      linkElement.click()
      document.body.removeChild(linkElement)

      this.$message.success("数据导出成功")
    },

    // 格式化电压
    formatVoltage(value) {
      if (value === undefined || value === null) return "-- "
      return `${Number(value).toFixed(1)} V`
    },

    // 格式化电流
    formatCurrent(value) {
      if (value === undefined || value === null) return "-- "
      return `${Number(value).toFixed(1)} A`
    },

    // 格式化功率
    formatPower(value) {
      if (value === undefined || value === null) return "-- "
      return `${Number(value).toFixed(2)} kW`
    },

    // 格式化温度
    formatTemp(value) {
      if (value === undefined || value === null) return "-- "
      return `${Number(value).toFixed(1)} °C`
    },

    //格式化时间
    formatElectricity(value) {
      console.log("kanzheli")
      if (value === undefined || value === null || value === "") return "--"
      // if (typeof value === "string" && value.length === 14) {
      //   return this.parseElectricityTimestamp(value)
      // }
      return `${value} `
    },

    // 格式化总电量
    formatTotalElectricity(value) {
      if (value === undefined || value === null) return "-- kWh"
      const numValue = Number(value)
      if (isNaN(numValue)) return "-- kWh"
      return `${(numValue / 1000).toFixed(2)} kWh`
    },

    // 格式化日期时间
    formatDateTime(date) {
      if (!date) return ""
      const d = new Date(date)
      const year = d.getFullYear()
      const month = (d.getMonth() + 1).toString().padStart(2, "0")
      const day = d.getDate().toString().padStart(2, "0")
      const hours = d.getHours().toString().padStart(2, "0")
      const minutes = d.getMinutes().toString().padStart(2, "0")
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    // 格式化API时间格式
    formatDateTimeForApi(date) {
      if (!date) return ""
      const d = new Date(date)
      const year = d.getFullYear()
      const month = (d.getMonth() + 1).toString().padStart(2, "0")
      const day = d.getDate().toString().padStart(2, "0")
      const hours = d.getHours().toString().padStart(2, "0")
      const minutes = d.getMinutes().toString().padStart(2, "0")
      const seconds = d.getSeconds().toString().padStart(2, "0")
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 格式化时间索引
    formatTimeIndex(index, total, startTime, endTime) {
      if (!startTime || !endTime) {
        return this.formatTimeIndexOld(index, total)
      }

      const timeSpan = endTime.getTime() - startTime.getTime()
      const interval = timeSpan / (total - 1)
      const time = new Date(startTime.getTime() + index * interval)

      if (timeSpan <= 2 * 60 * 60 * 1000) {
        // 2小时内，显示到分钟
        const hours = time.getHours().toString().padStart(2, "0")
        const minutes = time.getMinutes().toString().padStart(2, "0")
        return `${hours}:${minutes}`
      } else if (timeSpan <= 24 * 60 * 60 * 1000) {
        // 24小时内
        const hours = time.getHours().toString().padStart(2, "0")
        const minutes = time.getMinutes().toString().padStart(2, "0")
        return `${hours}:${minutes}`
      } else {
        // 超过24小时
        const year = time.getFullYear()
        const month = (time.getMonth() + 1).toString().padStart(2, "0")
        const day = time.getDate().toString().padStart(2, "0")
        const hours = time.getHours().toString().padStart(2, "0")
        return `${month}-${day} ${hours}:00`
      }
    },

    // 旧的格式化时间索引方法（兼容）
    formatTimeIndexOld(index, total) {
      const now = new Date()
      let interval

      if (this.timeRange === "24h" || this.timeRange === "6h" || this.timeRange === "1h") {
        interval = 3600000
      } else {
        interval = (24 * 3600000) / total
      }

      const time = new Date(now.getTime() - (total - index - 1) * interval)
      const year = time.getFullYear()
      const month = (time.getMonth() + 1).toString().padStart(2, "0")
      const day = time.getDate().toString().padStart(2, "0")
      const hours = time.getHours().toString().padStart(2, "0")
      const minutes = time.getMinutes().toString().padStart(2, "0")

      if (this.timeRange === "24h" || this.timeRange === "6h" || this.timeRange === "1h") {
        return `${hours}:${minutes}`
      } else {
        return `${year}-${month}-${day} ${hours}:00`
      }
    },

    // 解析电量时间戳
    parseElectricityTimestamp(timestamp) {
      if (!timestamp || timestamp.length !== 14) return "--"
      const year = timestamp.substring(0, 4)
      const month = timestamp.substring(4, 6)
      const day = timestamp.substring(6, 8)
      const hour = timestamp.substring(8, 10)
      const minute = timestamp.substring(10, 12)
      const second = timestamp.substring(12, 14)
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    },

    // 关闭弹窗
    closeDialog() {
      this.disposeCharts()
      this.resetData()
      this.$emit("update:show", false)
    }
  },
  beforeDestroy() {
    this.disposeCharts()
  }
}
</script>

<style scoped lang="scss">
.mb-20 {
  margin-bottom: 20px;
}

.bold-text {
  font-weight: bold;
  font-size: 16px;
}

.red-text {
  color: #f56c6c;
}

.charts-container {
  display: grid;
  width: 100%;
  gap: 20px;
  margin-top: 10px;
}

.curve-chart ::v-deep .el-descriptions__title::after {
  content: none !important;
}

.curve-chart ::v-deep .el-descriptions-item__label::after {
  content: "" !important;
}

.chart-item {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.chart-title {
  padding: 12px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.chart-wrapper {
  height: 260px;
  width: 100%;
}
::v-deep .el-descriptions__header {
  margin-bottom: 10px;
}
::v-deep .el-dialog__body {
  padding: 10px 20px;
}
::v-deep .el-descriptions--medium.is-bordered .el-descriptions-item__cell {
  padding: 9px 10px;
}
::v-deep .chart-title {
  padding: 9px 20px;
}
</style>
